import React, { Fragment, useMemo, useState } from "react";
import {
  Container,
  Alert,
  Badge,
  Button,
  Spinner,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  Info,
} from "react-feather";
import { Breadcrumbs } from "../../../AbstractElements";
import CatalogSyncIssuesService from "../../../Services/catalogSyncIssues/CatalogSyncIssuesService";
import { APP_PATHS } from "../../../utils/appPaths";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import "../Validation/validation.scss";

const TYPE_LABEL = {
  isrc_conflict: "ISRC conflict",
  isrc_missing: "ISRC missing",
  upc_conflict: "UPC conflict",
  upc_missing: "UPC missing",
  product_missing_releasing_label: "Missing releasing label",
  contributor_role_unknown: "Unknown contributor role",
  deal_label_missing: "Deal label missing",
  isrc_placeholder_leftover: "ISRC leftover placeholder",
  upc_placeholder_leftover: "UPC leftover placeholder",
  contributor_red_id_missing: "Missing red contributor id",
};

const ProductDetailCard = ({ title, entity, emptyHint }) => {
  if (!entity) {
    return (
      <div className="border rounded p-3 bg-light">
        <strong className="d-block mb-1">{title}</strong>
        <span className="text-muted small">{emptyHint || "Not found in Accounting"}</span>
      </div>
    );
  }

  const product = entity.kind === "asset" ? entity.product : entity;
  const artistLabel = [
    product?.artist?.name || entity.artist?.name,
    product?.label?.name || entity.label?.name,
  ]
    .filter(Boolean)
    .join(" / ");

  return (
    <div className="border rounded p-3 h-100">
      <strong className="d-block mb-2">{title}</strong>
      {entity.missing_in_accounting && (
        <Badge color="secondary" className="mb-2">Missing in Accounting</Badge>
      )}
      <div className="small mb-1">
        <strong>Product:</strong>{" "}
        {product?.id ? (
          <Link to={APP_PATHS.productAssets(product.id)}>{product.name || "—"}</Link>
        ) : (
          product?.name || entity.name || "—"
        )}
      </div>
      <div className="small mb-1">
        <strong>CMS id:</strong> {product?.red_product_id || entity.red_product_id || entity.red_asset_id || "—"}
      </div>
      <div className="small mb-1">
        <strong>UPC:</strong> <code>{product?.upc || "—"}</code>
      </div>
      <div className="small mb-1">
        <strong>Artist / Label:</strong> {artistLabel || "—"}
      </div>
      {entity.kind === "asset" && (
        <div className="small mb-1">
          <strong>Asset:</strong> {entity.name || "—"} · ISRC <code>{entity.isrc || "—"}</code>
        </div>
      )}
      <div className="small mt-2">
        <strong>Assets ({(product?.assets || []).length}):</strong>
        {(product?.assets || []).length === 0 ? (
          <div className="text-muted">None</div>
        ) : (
          <ul className="mb-0 ps-3">
            {(product.assets || []).map((a) => (
              <li key={a.id || a.red_asset_id}>
                {a.name || "—"} · ISRC <code>{a.isrc || "—"}</code>
                {a.red_asset_id ? ` · CMS ${a.red_asset_id}` : ""}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const CatalogSyncIssuesPage = () => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("open");
  const [typeFilter, setTypeFilter] = useState("");
  const [detailIssue, setDetailIssue] = useState(null);
  const [detailFocus, setDetailFocus] = useState("challenger"); // challenger | holder

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ["catalog-sync-issues", statusFilter, typeFilter],
    queryFn: () =>
      CatalogSyncIssuesService.getIssues({
        status: statusFilter,
        type: typeFilter || undefined,
        limit: 100,
      }),
  });

  const acknowledgeMutation = useMutation({
    mutationFn: (id) => CatalogSyncIssuesService.acknowledge(id),
    onSuccess: () => {
      toast.success("Issue acknowledged — it will stay hidden unless data changes.");
      queryClient.invalidateQueries({ queryKey: ["catalog-sync-issues"] });
      setDetailIssue(null);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err.message || "Acknowledge failed");
    },
  });

  const confirmSameMutation = useMutation({
    mutationFn: ({ id, force }) => CatalogSyncIssuesService.confirmSame(id, { force }),
    onSuccess: () => {
      toast.success("Marked as same — issue hidden. Everything looks fine.");
      queryClient.invalidateQueries({ queryKey: ["catalog-sync-issues"] });
      setDetailIssue(null);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err.message || "Confirm same failed");
    },
  });

  const scanMutation = useMutation({
    mutationFn: () => CatalogSyncIssuesService.scan(),
    onSuccess: (res) => {
      toast.info(res?.message || "Scan complete");
      queryClient.invalidateQueries({ queryKey: ["catalog-sync-issues"] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err.message || "Scan failed");
    },
  });

  const issues = data?.issues || [];
  const counts = data?.counts || { open: 0, acknowledged: 0, by_type: {} };
  const overall = data?.status === "clear" ? "healthy" : "degraded";

  const statusMeta = useMemo(() => {
    if (overall === "healthy") {
      return {
        label: "Clear",
        color: "success",
        Icon: CheckCircle,
        message: "No open catalog sync issues. CMS codes are mirrored without inventing GEN/ACC placeholders.",
      };
    }
    return {
      label: "Action needed",
      color: "warning",
      Icon: AlertTriangle,
      message: "Open conflicts or missing UPC/ISRC from CMS. Fix in CMS, then re-sync — Accounting will not invent codes.",
    };
  }, [overall]);

  const StatusIcon = statusMeta.Icon;

  const openConflictDetail = (issue, focus = "challenger") => {
    setDetailIssue(issue);
    setDetailFocus(focus);
  };

  const renderCmsIdLink = (issue, redId, focus) => {
    if (!redId) return null;
    return (
      <button
        type="button"
        className="btn btn-link btn-sm p-0 align-baseline"
        style={{ fontSize: "inherit" }}
        onClick={() => openConflictDetail(issue, focus)}
      >
        {redId}
      </button>
    );
  };

  return (
    <Fragment>
      <Breadcrumbs mainTitle="Catalog Sync Issues" parent="Monitoring" title="Catalog Sync" />
      <Container fluid className="rd-validation">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <Input
              type="select"
              bsSize="sm"
              style={{ width: 140 }}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="open">Open</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="resolved">Resolved</option>
              <option value="all">All</option>
            </Input>
            <Input
              type="select"
              bsSize="sm"
              style={{ width: 200 }}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="">All types</option>
              {Object.entries(TYPE_LABEL).map(([k, label]) => (
                <option key={k} value={k}>{label}</option>
              ))}
            </Input>
          </div>
          <div className="d-flex gap-2">
            <Button
              color="light"
              size="sm"
              disabled={scanMutation.isPending}
              onClick={() => scanMutation.mutate()}
            >
              {scanMutation.isPending ? <Spinner size="sm" /> : "Clear leftovers"}
            </Button>
            <Button color="primary" size="sm" disabled={isFetching} onClick={() => refetch()}>
              {isFetching ? <Spinner size="sm" /> : <RefreshCw size={14} className="me-1" />}
              Refresh
            </Button>
          </div>
        </div>

        {error && (
          <Alert color="danger">
            Failed to load: {error?.response?.data?.message || error.message}
          </Alert>
        )}

        <Alert color={statusMeta.color} className="d-flex align-items-start gap-2">
          <StatusIcon size={18} className="mt-1" />
          <div>
            <strong>{statusMeta.label}</strong>
            <div className="small mb-0">{statusMeta.message}</div>
            <div className="small mt-1">
              Open: <Badge color="warning">{counts.open ?? 0}</Badge>{" "}
              Acknowledged: <Badge color="secondary">{counts.acknowledged ?? 0}</Badge>{" "}
              Resolved: <Badge color="success">{counts.resolved ?? 0}</Badge>
            </div>
          </div>
        </Alert>

        {isLoading ? (
          <div className="text-center py-5"><Spinner /></div>
        ) : (
          <div className="val-panel">
            <h6 className="mb-3">Issues ({issues.length})</h6>
            {issues.length === 0 ? (
              <p className="text-muted small mb-0">No issues for this filter.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Code</th>
                      <th>Entity</th>
                      <th>Artist / Label</th>
                      <th>Message</th>
                      <th>Source</th>
                      <th>Last seen</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {issues.map((issue) => {
                      const isUpcConflict = issue.issue_type === "upc_conflict";
                      const isSame = issue.comparison?.is_same;
                      return (
                        <tr key={issue.id}>
                          <td>
                            <Badge color={issue.status === "open" ? "warning" : "secondary"}>
                              {TYPE_LABEL[issue.issue_type] || issue.issue_type}
                            </Badge>
                            {isUpcConflict && isSame && (
                              <div className="mt-1">
                                <Badge color="success">1:1 match</Badge>
                              </div>
                            )}
                            {isUpcConflict && issue.comparison && !isSame && (
                              <div className="mt-1">
                                <Badge color="danger">Different</Badge>
                              </div>
                            )}
                          </td>
                          <td>
                            <code className="small">{issue.code || "—"}</code>
                          </td>
                          <td className="small">
                            {issue.entity?.product?.id ? (
                              <Link to={APP_PATHS.productAssets(issue.entity.product.id)}>
                                {issue.entity.name || issue.entity.product.name}
                              </Link>
                            ) : (issue.entity?.kind === "product" || issue.entity_type === "product") && issue.entity?.id ? (
                              <Link to={APP_PATHS.productAssets(issue.entity.id)}>
                                {issue.entity.name || issue.red_entity_id}
                              </Link>
                            ) : (
                              issue.entity?.name || issue.red_entity_id || "—"
                            )}
                            <div className="text-muted">
                              CMS id{" "}
                              {isUpcConflict
                                ? renderCmsIdLink(issue, issue.red_entity_id, "challenger")
                                : (issue.red_entity_id || "—")}
                              {issue.related_red_entity_id ? (
                                <>
                                  {" · held by "}
                                  {isUpcConflict
                                    ? renderCmsIdLink(issue, issue.related_red_entity_id, "holder")
                                    : issue.related_red_entity_id}
                                </>
                              ) : null}
                            </div>
                            {isUpcConflict && (
                              <Button
                                color="link"
                                size="sm"
                                className="p-0 mt-1"
                                onClick={() => openConflictDetail(issue, "challenger")}
                              >
                                Compare products
                              </Button>
                            )}
                          </td>
                          <td className="small">
                            {issue.artist_label || (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td className="small" style={{ maxWidth: 320 }}>
                            {issue.message}
                          </td>
                          <td className="small">{issue.source}</td>
                          <td className="small">
                            {issue.last_seen_at
                              ? new Date(issue.last_seen_at).toLocaleString()
                              : "—"}
                          </td>
                          <td className="text-nowrap">
                            {issue.status === "open" && isUpcConflict && isSame && (
                              <Button
                                color="success"
                                size="sm"
                                className="me-1 mb-1"
                                disabled={confirmSameMutation.isPending}
                                onClick={() => confirmSameMutation.mutate({ id: issue.id, force: false })}
                              >
                                Mark as same
                              </Button>
                            )}
                            {issue.status === "open" && (
                              <Button
                                color="success"
                                outline
                                size="sm"
                                className="mb-1"
                                disabled={acknowledgeMutation.isPending}
                                onClick={() => acknowledgeMutation.mutate(issue.id)}
                              >
                                Acknowledge
                              </Button>
                            )}
                            {issue.status === "acknowledged" && (
                              <span className="text-muted small">
                                <Info size={12} className="me-1" />
                                Confirmed
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <Modal
          isOpen={!!detailIssue}
          toggle={() => setDetailIssue(null)}
          size="lg"
          centered
        >
          <ModalHeader toggle={() => setDetailIssue(null)}>
            UPC {detailIssue?.code || "conflict"} — product details
            {detailFocus === "holder" ? " (holder)" : " (challenger)"}
          </ModalHeader>
          <ModalBody>
            {detailIssue && (
              <>
                {detailIssue.comparison && (
                  <Alert color={detailIssue.comparison.is_same ? "success" : "warning"} className="small">
                    {detailIssue.comparison.is_same
                      ? "Looks like the same product (1:1). You can mark as same to hide this issue."
                      : "Products look different — review names/assets/ISRCs, then fix the duplicate UPC in CMS."}
                    <div className="mt-1">
                      Match score: {detailIssue.comparison.score}/100
                      {" · "}
                      name {detailIssue.comparison.checks?.name_match ? "✓" : "✗"}
                      {" · "}
                      artist {detailIssue.comparison.checks?.artist_match ? "✓" : "✗"}
                      {" · "}
                      label {detailIssue.comparison.checks?.label_match ? "✓" : "✗"}
                      {" · "}
                      ISRCs {detailIssue.comparison.checks?.isrc_exact ? "✓ same" : (detailIssue.comparison.checks?.isrc_partial ? "~ overlap" : "✗")}
                    </div>
                  </Alert>
                )}
                <div className="row g-3">
                  <div className="col-md-6">
                    <ProductDetailCard
                      title={`Challenger CMS ${detailIssue.red_entity_id}`}
                      entity={detailIssue.entity}
                    />
                  </div>
                  <div className="col-md-6">
                    <ProductDetailCard
                      title={`Holder CMS ${detailIssue.related_red_entity_id}`}
                      entity={detailIssue.related_entity}
                      emptyHint="Holder product not found in Accounting"
                    />
                  </div>
                </div>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            {detailIssue?.status === "open" && detailIssue?.comparison?.is_same && (
              <Button
                color="success"
                disabled={confirmSameMutation.isPending}
                onClick={() => confirmSameMutation.mutate({ id: detailIssue.id, force: false })}
              >
                {confirmSameMutation.isPending ? <Spinner size="sm" /> : "Mark as same — hide issue"}
              </Button>
            )}
            {detailIssue?.status === "open" && detailIssue?.comparison && !detailIssue.comparison.is_same && (
              <Button
                color="warning"
                outline
                disabled={confirmSameMutation.isPending}
                onClick={() => {
                  if (window.confirm("Products do not look identical. Hide this issue anyway?")) {
                    confirmSameMutation.mutate({ id: detailIssue.id, force: true });
                  }
                }}
              >
                Force hide anyway
              </Button>
            )}
            {detailIssue?.status === "open" && (
              <Button
                color="secondary"
                outline
                disabled={acknowledgeMutation.isPending}
                onClick={() => acknowledgeMutation.mutate(detailIssue.id)}
              >
                Acknowledge
              </Button>
            )}
            <Button color="light" onClick={() => setDetailIssue(null)}>Close</Button>
          </ModalFooter>
        </Modal>
      </Container>
    </Fragment>
  );
};

export default CatalogSyncIssuesPage;
